import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Container,
  Grid,
  Paper,
  TextField,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import i18next from '../../../lang';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { IconButton } from '@mui/material';
import { adminStyles } from '../../../theme/adminStyles';
import { Category } from './Interfaces';

interface ProductFormData {
  categoryId: string;
  curriculumName: string;
  volume: string;
  title: string;
  code: string;
  price: string;
}

const ProductForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [categories, setCategories] = useState<Category[]>([]);
  const [formData, setFormData] = useState<ProductFormData>({
    categoryId: '',
    curriculumName: '',
    volume: '',
    title: '',
    code: '',
    price: '',
  });

  useEffect(() => {
    fetchCategories();
    if (id) {
      fetchProduct();
    }
  }, [id]);

  const fetchCategories = async () => {
    try {
      const response = await axios.get('/api/v1/admin/product-categories');
      setCategories(response.data);
    } catch (error) {
      console.error('Failed to get category list：', error);
    }
  };

  const fetchProduct = async () => {
    try {
      const response = await axios.get(`/api/v1/admin/products/${id}`);
      const product = response.data;
      setFormData({
        categoryId: product.category.id.toString(),
        curriculumName: product.curriculumName,
        volume: product.volume,
        title: product.title,
        code: product.code,
        price: product.price.toString(),
      });
    } catch (error) {
      console.error('Failed to fetch product:', error);
    }
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    const price = parseFloat(formData.price);
    if (isNaN(price) || price < 0) {
      alert(i18next.t('product.price_must_be_zero_or_positive'));
      return;
    }

    const productData = {
      ...formData,
      price,
      categoryId: id ? formData.categoryId : 1
    };

    try {
      if (id) {
        await axios.put(`/api/v1/admin/products/${id}`, productData);
      } else {
        await axios.post('/api/v1/admin/products', productData);
      }
      navigate('/admin/products');
    } catch (error) {
      console.error('Failed to save product:', error);
    }
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement | { name?: string; value: unknown }>) => {
    const { name, value } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name as string]: value,
    }));
  };

  return (
    <Container maxWidth="lg" sx={adminStyles.pageContainer}>
      {/* ヘッダー */}
      <Box sx={adminStyles.headerBox}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton
            onClick={() => navigate('/admin/products')}
            sx={{ bgcolor: '#f5f5f7', '&:hover': { bgcolor: '#e8e8e8' } }}
          >
            <ArrowBackIcon />
          </IconButton>
        </Box>
      </Box>

      <Paper sx={adminStyles.contentPaper}>
        <Box component="form" onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={i18next.t('product.curriculum_name')}
                name="curriculumName"
                value={formData.curriculumName}
                onChange={handleChange}
                required
                InputProps={{ style: { fontSize: '0.8rem' } }}
                InputLabelProps={{ style: { fontSize: '0.8rem' } }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                  fullWidth
                  label={i18next.t('common.title')}
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  required
                  InputProps={{ style: { fontSize: '0.8rem' } }}
                  InputLabelProps={{ style: { fontSize: '0.8rem' } }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={i18next.t('product.volume')}
                name="volume"
                value={formData.volume}
                onChange={handleChange}
                required
                InputProps={{ style: { fontSize: '0.8rem' } }}
                InputLabelProps={{ style: { fontSize: '0.8rem' } }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                  fullWidth
                  label={i18next.t('product.subject_code')}
                  name="code"
                  value={formData.code}
                  onChange={handleChange}
                  required
                  InputProps={{ style: { fontSize: '0.8rem' } }}
                  InputLabelProps={{ style: { fontSize: '0.8rem' } }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={i18next.t('product.price')}
                name="price"
                type="number"
                value={formData.price}
                onChange={handleChange}
                required
                inputProps={{ min: 0 }}
                InputProps={{ style: { fontSize: '0.8rem' } }}
                InputLabelProps={{ style: { fontSize: '0.8rem' } }}
              />
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                <Button
                  variant="outlined"
                  onClick={() => navigate('/admin/products')}
                  sx={{ fontSize: '0.8rem' }}
                >
                  {i18next.t('common.cancel')}
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  sx={{ fontSize: '0.8rem' }}
                >
                  {i18next.t('common.save')}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Paper>
    </Container>
  );
};

export default ProductForm;